import { Link, Route, Routes } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Billing, Plans } from '@/features/billing';
import { AccountSuspendedScreen } from './AccountSuspendedScreen';
import { usePermission } from '@/hooks/usePermission';
import { useSuspensionSummary } from '@/hooks/useSuspensionSummary';
import { config } from '@/config';

/**
 * The entire application while an account is suspended.
 *
 * Deliberately not `AppShell`: the normal chrome advertises Devices, Gateways,
 * Alerts and the rest, none of which will load, and a nav full of dead ends
 * both reads as broken and softens the point. What remains is the lockout
 * screen plus the two pages that can end the lockout.
 *
 * Every other path — including deep links someone had bookmarked — falls
 * through to the lockout screen rather than 404ing, so there is exactly one
 * thing to do from anywhere in the app.
 */
export function SuspendedApp() {
  const canManageBilling = usePermission('billing:read');

  // Polled here rather than only on the lockout screen: the screen unmounts the
  // moment someone navigates to the billing page, which is precisely when the
  // account is about to come good. Watching from the top means the lockout
  // lifts the instant payment lands, on whichever page they happen to be.
  useSuspensionSummary();

  return (
    <div className="min-h-svh bg-background">
      <Routes>
        {canManageBilling ? (
          <>
            <Route path="/admin/billing" element={<BillingChrome><Billing /></BillingChrome>} />
            <Route path="/admin/plans" element={<BillingChrome><Plans /></BillingChrome>} />
          </>
        ) : null}
        <Route path="*" element={<AccountSuspendedScreen />} />
      </Routes>
    </div>
  );
}

/**
 * Minimal frame around the billing pages: brand, and a way back to the lockout
 * screen. Without it those pages render with no header at all and no exit.
 */
function BillingChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-svh flex flex-col">
      <header className="border-b border-border">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-14 flex items-center gap-3">
          <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold">
            {config.appName.charAt(0)}
          </div>
          <span className="font-semibold tracking-tight">{config.appName}</span>
          <Link
            to="/"
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </Link>
        </div>
      </header>
      <div className="flex-1 p-4 md:p-6">{children}</div>
    </div>
  );
}
